import questionnaire from '../config/questionnaire';
import methodology from '../config/methodology';
import { normalizeDestination } from './destinations';
import { normalizeMarketTrend } from './marketSignals';

const cloneData = (data = {}) => JSON.parse(JSON.stringify(data || {}));

export const isSubmissionCountable = (submission = {}) => !submission.excludedFromResults;

const sanitizeSubmissionForPublicHistory = (submission, index) => {
  const participantKey = `participante_${String(index + 1).padStart(2, '0')}`;

  return {
    companyId: participantKey,
    companyName: 'Empresa participante',
    exportType: submission.exportType || 'Sin dato',
    otherExportTypeDetail: submission.otherExportTypeDetail || '',
    destination: normalizeDestination(submission.destination),
    month: submission.month || '',
    data: cloneData(submission.data),
    navieraPrincipal: submission.navieraPrincipal || submission.data?.meta_naviera_principal || 'Sin dato',
    port: submission.port || 'Otro puerto',
    trend: normalizeMarketTrend(submission.trend || submission.data?.d2_q5),
    rentabilidadTrend: submission.rentabilidadTrend || '',
  };
};

export const calculateAggregatedResults = (submissions) => {
  const countableSubmissions = submissions.filter(isSubmissionCountable);
  if (!countableSubmissions.length) return null;

  const allResults = countableSubmissions.map((submission) => methodology.calculateIndices(submission.data));
  const dimensionalAccumulator = {};
  const questionAccumulator = {};

  questionnaire.getDimensions().forEach((dimension) => {
    dimensionalAccumulator[dimension.id] = [];
  });

  questionnaire.getAllQuestions().filter((q) => q.type === 'likert' || q.type === 'binary').forEach((question) => {
    questionAccumulator[question.id] = [];
  });

  allResults.forEach((result) => {
    Object.entries(result.dimensionalIndices).forEach(([dimensionId, value]) => {
      if (value !== null) dimensionalAccumulator[dimensionId].push(value);
    });
    Object.entries(result.questionNormalized).forEach(([questionId, value]) => {
      if (value !== null && questionAccumulator[questionId]) {
        questionAccumulator[questionId].push(value);
      }
    });
  });

  const dimensionalIndices = {};
  Object.entries(dimensionalAccumulator).forEach(([dimensionId, values]) => {
    dimensionalIndices[dimensionId] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  });

  const questionAverages = {};
  Object.entries(questionAccumulator).forEach(([questionId, values]) => {
    questionAverages[questionId] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  });

  let globalIndex = 0;
  questionnaire.getDimensions().forEach((dimension) => {
    globalIndex += (dimensionalIndices[dimension.id] || 0) * dimension.weight;
  });

  return {
    globalIndex: parseFloat(globalIndex.toFixed(2)),
    dimensionalIndices: Object.fromEntries(
      Object.entries(dimensionalIndices).map(([dimensionId, value]) => [dimensionId, parseFloat(value.toFixed(2))]),
    ),
    questionAverages,
    participantCount: countableSubmissions.length,
  };
};

export const buildPublishedEntry = (submissions, period, options = {}) => {
  const { publicSafe = true } = options;
  const countableSubmissions = submissions.filter(isSubmissionCountable);
  const aggregate = calculateAggregatedResults(countableSubmissions);
  if (!aggregate) return null;
  const sourceSubmissions = publicSafe
    ? countableSubmissions.map(sanitizeSubmissionForPublicHistory)
    : countableSubmissions.map((submission) => ({ ...submission, data: cloneData(submission.data) }));

  return {
    ...aggregate,
    period,
    sourceSubmissions,
  };
};
